import axios from 'axios';

declare global {
  interface Window {
    __DASHBAL_CONFIG__?: {
      apiBaseUrl?: string
      wsUrl?: string
      skipEmailVerification?: boolean
      enableDebug?: boolean
      isProduction?: boolean
      tiktokClientKey?: string
      facebookAppId?: string
      facebookConfigId?: string
      googleClientId?: string
      appleClientId?: string
    }
  }
}

// Get API base URL from runtime config (injected by server.js)
// In production: https://api.dashbal.com
// In development: http://localhost:3001
const getApiBaseUrl = (): string => {
    // 1. Runtime config injected by server.js (Production & Docker)
    if (typeof window !== 'undefined' && window.__DASHBAL_CONFIG__?.apiBaseUrl) {
        return window.__DASHBAL_CONFIG__.apiBaseUrl;
    }

    // 2. Build-time environment variable (esbuild define)
    if (process.env.API_BASE_URL) {
        return process.env.API_BASE_URL;
    }

    // 3. Development Fallback
    if (process.env.NODE_ENV !== 'production') {
        return 'http://localhost:3001';
    }

    // 4. Production Fallback
    console.warn('API URL not configured. Defaulting to current origin.');
    return window.location.origin;
};

// Create a separate axios instance for maintenance endpoints
// This bypasses the normal auth interceptors since we need to check
// maintenance status before any other requests
const maintenanceClient = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 10000,
    withCredentials: true, // Important: This sends cookies with requests
    headers: {
        'Content-Type': 'application/json',
    },
});

export interface MaintenanceStatus {
    maintenanceMode: boolean;
    hasAccess: boolean;
}

export interface MaintenanceVerifyResponse {
    message: string;
    expiresIn: string;
}

/**
 * Check if maintenance mode is enabled and if current browser has access
 */
export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
    try {
        const response = await maintenanceClient.get('/maintenance/status');
        if (response.data.success) {
            return response.data.data;
        }
        throw new Error('Failed to get maintenance status');
    } catch (error: any) {
        // If we can't reach the server, assume no maintenance mode
        console.error('Failed to check maintenance status:', error);
        return {
            maintenanceMode: false,
            hasAccess: true,
        };
    }
}

/**
 * Verify access code and get a maintenance access token
 */
export async function verifyMaintenanceCode(code: string): Promise<MaintenanceVerifyResponse> {
    const response = await maintenanceClient.post('/maintenance/verify', { code });
    if (response.data.success) {
        return response.data.data;
    }
    throw new Error(response.data.error || 'Verification failed');
}

/**
 * Revoke maintenance access (logout from maintenance mode)
 */
export async function revokeMaintenanceAccess(): Promise<void> {
    await maintenanceClient.post('/maintenance/revoke');
}
